import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type {
  Message,
  MessageWithSupportTicketId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type {
  SupportTicketThreadContext,
  SupportTicketThreadResponse,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import {
  getSupportTicketIdScope,
  type SupportTicketCollectionFilter,
} from "./support-ticket-collection-scope";

export type SupportTicketThreadCollectionItem = ChannelThread & {
  supportTicketId: SupportTicketId;
  ticketContext?: SupportTicketThreadContext;
};

export interface SupportTicketTranscriptApi {
  getThreads(
    supportTicketId: SupportTicketId,
  ): Promise<{ items: readonly SupportTicketThreadResponse[] }>;
  getMessages(
    supportTicketId: SupportTicketId,
  ): Promise<{ items: readonly Message[] }>;
  getTimelineEvents(
    supportTicketId: SupportTicketId,
  ): Promise<{ items: readonly SupportTicketTimelineEventResponse[] }>;
}

export function getSupportTicketIdFilter(
  filters: readonly SupportTicketCollectionFilter[],
): SupportTicketId | null {
  return getSupportTicketIdScope(filters);
}

export async function fetchSupportTicketThreads(
  api: SupportTicketTranscriptApi,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketThreadCollectionItem[]> {
  const result = await api.getThreads(supportTicketId);
  return result.items.map(({ thread, ticketContext }) => ({
    ...thread,
    supportTicketId,
    ...(ticketContext ? { ticketContext } : {}),
  }));
}

export async function fetchSupportTicketMessages(
  api: SupportTicketTranscriptApi,
  supportTicketId: SupportTicketId,
): Promise<MessageWithSupportTicketId[]> {
  const result = await api.getMessages(supportTicketId);
  return result.items.map((message) => ({ ...message, supportTicketId }));
}
